export class ApiError extends Error {
  readonly status: number;
  readonly fieldErrors: Record<string, string | string[]>;

  constructor(
    message: string,
    status = 500,
    fieldErrors: Record<string, string | string[]> = {},
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.fieldErrors = fieldErrors;
  }
}

function flatten(value: unknown): string[] {
  if (value == null) return [];
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) return value.flatMap(flatten);
  if (typeof value === "object") {
    return Object.values(value).flatMap(flatten);
  }
  return [String(value)];
}

export function parseApiError(body: unknown, httpStatus?: number): ApiError {
  if (typeof body === "string") {
    return new ApiError(body, httpStatus ?? 500);
  }

  if (!body || typeof body !== "object") {
    return new ApiError("An unexpected error occurred", httpStatus ?? 500);
  }

  const record = body as Record<string, unknown>;
  const errorBlock =
    record.error && typeof record.error === "object" && !Array.isArray(record.error)
      ? (record.error as Record<string, unknown>)
      : record;

  const fieldErrors: Record<string, string | string[]> = {};
  for (const [key, value] of Object.entries(errorBlock)) {
    if (key === "message" || key === "success" || key === "status" || key === "results") {
      continue;
    }
    const parts = flatten(value);
    if (parts.length) {
      fieldErrors[key] = parts.length === 1 ? parts[0] : parts;
    }
  }

  const nonField = flatten(errorBlock.non_field_errors);
  // Prefer concrete field / non-field errors over generic envelope text ("Bad Request").
  const fieldMessage = flatten(Object.values(fieldErrors))[0];
  const envelopeMessage =
    typeof record.message === "string" ? record.message.trim() : "";
  const message =
    nonField[0] ||
    fieldMessage ||
    envelopeMessage ||
    "An unexpected error occurred";

  const status =
    (typeof record.status === "number" ? record.status : undefined) ?? httpStatus ?? 500;

  return new ApiError(message, status, fieldErrors);
}
